import {
  QuestionDto,
  CategoryResponseDto,
} from 'enrollment/queries/useBenefitCategoryQuery/types';
import {
  FilteredResponseDataDto,
  SurveyResponseDto,
  UserResponseDto,
} from '../types';
import {
  DATE_SURVEY_TYPE,
  HEIGHT_SURVEY_TYPE,
  MULTI_CHOICE_SURVEY_TYPE,
  MULTI_SELECT_SURVEY_TYPE,
  NUMBER_SURVEY_TYPE,
  SUB_AND_DEP_TYPE,
  TEXT_SURVEY_TYPE,
} from './constants';
import { Spacer } from 'theSource/components';
import { DependentDto } from 'theSource/features/FamilyMembers/types';
import { FamilyMemberReadonly } from 'enrollment/features/Shared';
import {
  FamilyMemberContainerStyled,
  HeightContainerDependentStyled,
} from '../QuestionnaireStyled';
import { BenefitRestrictions } from 'enrollment/queries/useRestrictionsQuery/types';
import { LifeEventRestriction } from 'enrollment/utils/LifeEventUtils/Utils';
import {
  PsCheckboxTuner,
  PsNumericTuner,
  PsRadioTuner,
  PsTextTuner,
} from 'stencilSetup';
import { BenefitDto } from 'enrollment/queries/useBenefitPlansQuery/types';

export const isInvalidData = (
  questionResponse: CategoryResponseDto | undefined
) => {
  return questionResponse === undefined ? true : false;
};

export const initResponses = (
  flattenedData: QuestionDto[],
  questionResponse: CategoryResponseDto | undefined
) => {
  if (isInvalidData(questionResponse)) {
    return [];
  }

  const data = questionResponse as CategoryResponseDto;

  const savedResponses = filterUserResponses(data);
  const responses: UserResponseDto[] = flattenedData.map((question) => {
    let responseValue = savedResponses.map((response) => {
      // will check if user has answered a multi_choice/multi_select question
      if (
        question.valid_responses?.some((validResponse) => {
          return validResponse.response_id === response.response_id;
        })
      ) {
        return String(response.response_id);
        // will check if user has answered a text/text-adjacent question
      } else if (response.question_id === question.question_id) {
        return response.response;
      } else {
        return '';
      }
    });
    responseValue = responseValue.filter((val) => {
      return val !== '';
    });
    return { question_id: question.question_id, response: responseValue };
  });

  return responses;
};

export const generateQuestionForm = (
  question: QuestionDto,
  questionResponse: CategoryResponseDto | undefined,
  response?: SurveyResponseDto,
  dependents?: DependentDto[],
  valueLabel?: string,
  disabled?: boolean,
  onChange?: (e: any) => void
) => {
  if (isInvalidData(questionResponse)) {
    return [];
  }
  const data = questionResponse as CategoryResponseDto;

  const userResponses = filterUserResponses(data);
  let responseValue = '';
  let responseId = -1;
  if (response) {
    // checking to see if user has already submitted a response (radio, checkbox)
    responseValue = userResponses.find((userRes) => {
      return userRes.response_id === response.response_id;
    })
      ? response.value
      : '';
    responseId = response.response_id;
  } else {
    // checking to see if user has already submitted a response (input)
    responseValue =
      userResponses
        .find((userRes) => {
          return (
            userRes.question_id === question.question_id &&
            !userRes.dependent_id // we only want subscriber answers
          );
        })
        ?.response.toString() || '';
  }

  if (question.dependents_in_population) {
    if (question.targeted_at_type === SUB_AND_DEP_TYPE) {
      return (
        <>
          {question.dependents_in_population.map((depId) => {
            const dependent = dependents?.find((dep) => {
              return dep.id === depId;
            });

            // have to set new value for dependent response
            const depResponse = getDependentResponseValue(
              userResponses,
              depId,
              question
            );

            return (
              <>
                {generateDependentIcon(dependent, question.question_id)}
                <HeightContainerDependentStyled
                  $isHeight={question.question_type === HEIGHT_SURVEY_TYPE}
                >
                  {generateFormField(
                    question,
                    responseId,
                    depResponse,
                    response,
                    valueLabel,
                    depId,
                    disabled,
                    onChange
                  )}
                </HeightContainerDependentStyled>
              </>
            );
          })}
          {question.dependents_in_population.length > 0 && (
            <FamilyMemberContainerStyled>
              <FamilyMemberReadonly
                isYou={true}
                key={`you_q${question.question_id}`}
              />
            </FamilyMemberContainerStyled>
          )}
          <HeightContainerDependentStyled
            $isHeight={question.question_type === HEIGHT_SURVEY_TYPE}
          >
            {generateFormField(
              question,
              responseId,
              responseValue,
              response,
              valueLabel,
              undefined,
              disabled,
              onChange
            )}
          </HeightContainerDependentStyled>
        </>
      );
    } else {
      return question.dependents_in_population.map((depId) => {
        const dependent = dependents?.find((dep) => {
          return dep.id === depId;
        });

        // have to set new value for dependent response
        const depResponse = getDependentResponseValue(
          userResponses,
          depId,
          question
        );

        return (
          <>
            {generateDependentIcon(dependent, question.question_id)}
            {generateFormField(
              question,
              responseId,
              depResponse,
              response,
              valueLabel,
              depId,
              disabled,
              onChange
            )}
          </>
        );
      });
    }
  } else {
    return generateFormField(
      question,
      responseId,
      responseValue,
      response,
      valueLabel,
      undefined,
      disabled,
      onChange
    );
  }
};

const generateFormField = (
  question: QuestionDto,
  responseId: number,
  responseValue: string,
  response?: SurveyResponseDto,
  valueLabel?: string,
  depId?: number,
  disabled?: boolean,
  onChange?: (e: any) => void
) => {
  switch (question.question_type) {
    case DATE_SURVEY_TYPE:
      return (
        <PsTextTuner
          key={`q${question.question_id}_r${responseId}_dep${
            depId ? depId : ''
          }`}
          inputId={`q${question.question_id}_r${responseId}_dep${
            depId ? depId : ''
          }`}
          label={String(valueLabel)}
          type="date"
          name={responseValue}
          value={formatDateString(responseValue)}
          required={!question.is_optional}
          disabled={disabled}
          onInput={onChange}
        />
      );
    case NUMBER_SURVEY_TYPE:
      return (
        <PsNumericTuner
          key={`q${question.question_id}_r${responseId}_dep${
            depId ? depId : ''
          }`}
          inputId={`q${question.question_id}_r${responseId}_dep${
            depId ? depId : ''
          }`}
          label={String(valueLabel)}
          name={responseValue}
          value={responseValue}
          max={String(question.max_value)}
          min={String(question.min_value)}
          required={!question.is_optional}
          help-text={addHelperTextToField(question)}
          disabled={disabled}
          onInput={onChange}
        />
      );
    case TEXT_SURVEY_TYPE:
      return (
        <PsTextTuner
          key={`q${question.question_id}_r${responseId}_dep${
            depId ? depId : ''
          }`}
          inputId={`q${question.question_id}_r${responseId}_dep${
            depId ? depId : ''
          }`}
          name={responseValue}
          label={String(valueLabel)}
          value={responseValue}
          required={!question.is_optional}
          disabled={disabled}
          onInput={onChange}
        />
      );
    case MULTI_SELECT_SURVEY_TYPE: // checkbox
      return (
        <PsCheckboxTuner
          key={`q${question.question_id}_r${responseId}_dep${
            depId ? depId : ''
          }`}
          inputId={`q${question.question_id}_r${responseId}_dep${
            depId ? depId : ''
          }`}
          name={`q${question.question_id}_r${responseId}`}
          label={String(response?.value)}
          value={response?.value}
          checked={responseValue !== ''}
          other-data={`data-display-order="${response?.display_order}"`}
          extraClass="mr-4"
          disabled={disabled}
          onClick={onChange}
        />
      );
    case MULTI_CHOICE_SURVEY_TYPE: // radio
      return (
        <PsRadioTuner
          key={`q${question.question_id}_r${responseId}_dep${
            depId ? depId : ''
          }`}
          inputId={`q${question.question_id}_r${responseId}_dep${
            depId ? depId : ''
          }`}
          name={`radio-${question.question_id}`}
          label={String(response?.value)}
          value={response?.value}
          checked={responseValue !== ''}
          onClick={onChange}
          other-data={`data-display-order="${response?.display_order}"`}
          disabled={disabled}
          extraClass="mr-4"
        />
      );
    case HEIGHT_SURVEY_TYPE:
      const [heightFt, heightIn] = getHeightValuesFromResponse(responseValue);
      const minHeightFt = getHeightValuesFromResponse(String(question.min_value))[0];
      const maxHeightFt = getHeightValuesFromResponse(String(question.max_value))[0];

      const handleHeightChange = (e: any, isFeet: boolean) => {
        // Always coerce to 0 if empty or undefined
        const inputValue = e.target.value === '' ? '0' : e.target.value;
        const ft = isFeet ? Number(inputValue) : heightFt;
        const inches = isFeet ? heightIn : Number(inputValue);
        const totalInches = (ft * 12) + inches;
        
        if (onChange) {
          onChange({
            target: {
              value: String(totalInches),
              id: `q${question.question_id}_r${responseId}`,
              isValid: e.target.validity.valid, // Ensure the input is valid
            }
          });
        }
      };

      const componentKeyFeet = `ft-q${question.question_id}_r${responseId}_dep${
        depId ? depId : ''
      }`;

      const componentKeyInches = `inch-q${question.question_id}_r${responseId}_dep${
        depId ? depId : ''
      }`;
      return (
        <>
          <PsNumericTuner
            key={componentKeyFeet}
            inputId={componentKeyFeet}
            label="Foot"
            name={componentKeyFeet}
            value={heightFt?.toString() || '0'}
            min={String(minHeightFt)}
            max={String(maxHeightFt)}
            root-class="w-50"
            required={!question.is_optional}
            disabled={disabled}
            onInput={(e) => handleHeightChange(e, true)}
          />
          <Spacer width={6} />
          <PsNumericTuner
            key={componentKeyInches}
            inputId={componentKeyInches}  
            label="Inches"
            name={componentKeyInches}
            value={heightIn?.toString() || '0'}
            min="0"
            max="12"
            root-class="w-50"
            required={!question.is_optional}
            disabled={disabled}
            onInput={(e) => handleHeightChange(e, false)}
          />
        </>
      );
  }
};

export const generateDependentMultipleQuestions = (
  question: QuestionDto,
  questionResponse: CategoryResponseDto | undefined,
  depId: number | undefined,
  response: SurveyResponseDto,
  disabled?: boolean,
  onChange?: (e: any) => void
) => {
  if (isInvalidData(questionResponse)) {
    return [];
  }
  const data = questionResponse as CategoryResponseDto;

  const userResponses = filterUserResponses(data);

  const responseId = response.response_id;
  const responseValue = userResponses.find((userRes) => {
    return (
      userRes.response_id === response.response_id &&
      userRes.dependent_id === depId
    );
  })
    ? response.value
    : '';

  switch (question.question_type) {
    case MULTI_SELECT_SURVEY_TYPE: // checkbox
      return (
        <PsCheckboxTuner
          key={`q${question.question_id}_r${responseId}_dep${
            depId ? depId : ''
          }`}
          inputId={`q${question.question_id}_r${responseId}_dep${
            depId ? depId : ''
          }`}
          name={`q${question.question_id}_r${responseId}`}
          label={response?.value}
          value={response?.value}
          checked={responseValue !== ''}
          other-data={`data-display-order="${response?.display_order}"`}
          disabled={disabled}
          onClick={onChange}
        />
      );
    case MULTI_CHOICE_SURVEY_TYPE: // radio
      return (
        <PsRadioTuner
          key={`q${question.question_id}_r${responseId}_dep${
            depId ? depId : ''
          }`}
          inputId={`q${question.question_id}_r${responseId}_dep${
            depId ? depId : ''
          }`}
          name={`radio-${question.question_id}${depId ? '_dep' + depId : ''}`}
          label={response?.value}
          value={response?.value}
          checked={responseValue !== ''}
          onClick={onChange}
          disabled={disabled}
          other-data={`data-display-order="${response?.display_order}"`}
        />
      );
  }
};

export const findParentIndex = (
  parentId: number,
  flattenedData: QuestionDto[]
) => {
  return flattenedData.findIndex(
    (questions) => questions.question_id === parentId
  );
};

export const findQuestion = (
  questionId: number,
  flattenedData: QuestionDto[]
) => {
  const question = flattenedData.find(
    (questions) => questions.question_id === questionId
  );
  return question;
};

const filterResponseData = (questionResponse: CategoryResponseDto) => {
  return questionResponse.data.benefits.filter((benefit) => {
    return (
      benefit.org_benefit_survey_questions &&
      benefit.org_benefit_survey_questions.length > 0
    );
  });
};

const filterUserResponses = (questionResponse: CategoryResponseDto) => {
  const retval: FilteredResponseDataDto[] = [];
  questionResponse.data.benefits.forEach((benefit) => {
    if (
      benefit.coverage === null ||
      benefit.coverage === undefined ||
      benefit.coverage.survey_responses === undefined
    ) {
      return [];
    } else {
      benefit.coverage.survey_responses.forEach((surveyResponse) => {
        if (Array.isArray(surveyResponse.responses.subscriber_response)) {
          surveyResponse.responses.subscriber_response.forEach((response) => {
            retval.push({
              question_id: surveyResponse.question_id,
              response: response.value,
              response_id: response.response_id,
            });
          });
        }
        if (surveyResponse.responses.dependent_responses) {
          const question = benefit.org_benefit_survey_questions?.find(
            (questions) => {
              return questions.question_id === surveyResponse.question_id;
            }
          );

          if (
            ![MULTI_SELECT_SURVEY_TYPE, MULTI_CHOICE_SURVEY_TYPE].includes(
              String(question?.question_type)
            )
          ) {
            Object.entries(
              surveyResponse.responses.dependent_responses
            ).forEach(([key, value]) =>
              retval.push({
                question_id: surveyResponse.question_id,
                response: value,
                response_id: -1,
                dependent_id: Number(key),
              })
            );
          } else {
            // checkbox / radio questions
            Object.entries(
              surveyResponse.responses.dependent_responses
            ).forEach(([key, value]) =>
              value.forEach((mcResponse: SurveyResponseDto) => {
                retval.push({
                  question_id: surveyResponse.question_id,
                  response: mcResponse.value,
                  response_id: mcResponse.response_id,
                  dependent_id: Number(key),
                });
              })
            );
          }
        }
        if (
          surveyResponse.responses.subscriber_response &&
          !Array.isArray(surveyResponse.responses.subscriber_response)
        ) {
          retval.push({
            question_id: surveyResponse.question_id,
            response: surveyResponse.responses.subscriber_response as string,
            response_id: -1,
          });
        }
        return retval as unknown as FilteredResponseDataDto[];
      });
    }
  });

  return retval;
};

export const removeBenefitsThatAreRestricted = (
  lifeEventRestrictions: BenefitRestrictions | BenefitRestrictions[],
  orgBenefits: CategoryResponseDto
): [CategoryResponseDto, number[]] => {
  const restrictedOrgBenefitIds: number[] = [];
  const disabledOrgBenefitIds: number[] = [];
  orgBenefits.data.benefits.forEach((orgBenefit) => {
    const orgBenefitId = orgBenefit.benefit.org_benefit_id;
    const lifeEventRestriction = new LifeEventRestriction({
      lifeEventRestrictions: lifeEventRestrictions,
      orgBenefit: orgBenefit.benefit as BenefitDto,
      currentCoverage: orgBenefit.coverage || undefined,
    });
    if (orgBenefit.coverage && !lifeEventRestriction.canChangePlan) {
      disabledOrgBenefitIds.push(orgBenefitId);
    } else if (
      orgBenefit.plans?.length === 0 ||
      (!orgBenefit.coverage && !lifeEventRestriction.canElect)
    ) {
      restrictedOrgBenefitIds.push(orgBenefitId);
    }
  });
  const filteredBenefits = orgBenefits.data.benefits.filter(
    (orgBenefits) =>
      restrictedOrgBenefitIds.indexOf(orgBenefits.benefit.org_benefit_id) === -1
  );

  const newOrgBenefits: CategoryResponseDto = {
    ...orgBenefits,
    data: {
      ...orgBenefits.data,
      benefits: filteredBenefits,
    },
  };
  return [newOrgBenefits, disabledOrgBenefitIds];
};

export const flattenQuestionData = (
  questionResponse: CategoryResponseDto | undefined
) => {
  if (isInvalidData(questionResponse)) {
    return [];
  }
  const data = questionResponse as CategoryResponseDto;
  const filteredData = filterResponseData(data);
  const flattenedData = filteredData.map((benefit) => {
    if (!benefit.org_benefit_survey_questions) {
      return [];
    }
    return benefit.org_benefit_survey_questions.map((question) => {
      return {
        ...question,
      };
    });
  });
  return flattenedData.flat() as QuestionDto[];
};

export const formatDateString = (date: string) => {
  const dateArr = date.split('/');
  if (dateArr.length < 3) {
    return '';
  }
  return `${dateArr[0]}-${dateArr[1]}-${dateArr[2]}`;
};

export const generateDependentIcon = (
  dependent: DependentDto | undefined,
  questionId: number
) => {
  return (
    <FamilyMemberContainerStyled>
      <FamilyMemberReadonly
        name={dependent?.first_name}
        key={`${dependent?.id}_q${questionId}`}
        age={getDependentAge(dependent?.birthdate)}
      />
    </FamilyMemberContainerStyled>
  );
};

const getDependentAge = (birthday: string | undefined) => {
  const birthDate = new Date(String(birthday));
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const m = today.getMonth() - birthDate.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  return age;
};

const getDependentResponseValue = (
  userResponses: FilteredResponseDataDto[],
  depId: number,
  question: QuestionDto
) => {
  return (
    userResponses
      .find((res) => {
        return (
          res.dependent_id === depId && question.question_id === res.question_id
        );
      })
      ?.response.toString() || ''
  );
};

const getHeightValuesFromResponse = (value: string | undefined) => {
  if (value === undefined) {
    return [0, 0];
  }

  const heightInInches = Number(value);
  const heightFt = Math.floor(heightInInches / 12);
  const heightIn = heightInInches % 12;

  return [heightFt, heightIn];
};

export const allRequiredAnswered = (
  responses: UserResponseDto[],
  flattenedData: QuestionDto[]
) => {
  let allAnswered = true;
  let oneChoiceSelected = false;
  let isVisible = false;
  const form = document.getElementById('questionnaire-form');

  if (!form) {
    return checkFormFromInitValues(responses, flattenedData);
  }

  flattenedData.forEach((question) => {
    if (question.is_optional || !allAnswered) {
      return;
    }

    form
      .querySelectorAll(`input, [id*="q${question.question_id}"]`)
      .forEach((requiredInput) => {
        if (
          requiredInput.tagName === 'LABEL' ||
          requiredInput.tagName === 'DIV'
        ) {
          return;
        }

        const element = requiredInput as unknown as HTMLInputElement;
        const questionId = element.id.substring(
          element.id.indexOf('q') + 1,
          element.id.indexOf('_')
        );

        if (Number(questionId) !== question.question_id) {
          return;
        }
        // this will prevent validation on hidden required follow-up questions
        isVisible = true;

        if (
          [MULTI_CHOICE_SURVEY_TYPE, MULTI_SELECT_SURVEY_TYPE].includes(
            question.question_type
          )
        ) {
          if (element.checked) {
            oneChoiceSelected = true;
          }
        } else {
          if (!element.value || element.value === '') {
            allAnswered = false;
          }
        }
      });

    if (
      [MULTI_CHOICE_SURVEY_TYPE, MULTI_SELECT_SURVEY_TYPE].includes(
        question.question_type
      ) &&
      !oneChoiceSelected &&
      isVisible
    ) {
      allAnswered = false;
    }

    oneChoiceSelected = false;
    isVisible = false;
  });

  return allAnswered;
};

const checkFormFromInitValues = (
  responses: UserResponseDto[],
  flattenedData: QuestionDto[]
) => {
  let requiredAnswered = 0;
  let requiredQuestions = 0;

  flattenedData.forEach((question) => {
    if (question.is_optional || requiredAnswered !== requiredQuestions) {
      return;
    }

    requiredQuestions++;
    if (
      responses.some((res) => {
        // if the required question has a response
        return (
          question.question_id === res.question_id && res.response.length > 0
        );
      })
    ) {
      requiredAnswered++;
    }
  });

  return requiredAnswered === requiredQuestions;
};

const addHelperTextToField = (question: QuestionDto) => {
  if (question.max_value && question.min_value) {
    return `Please enter a value between ${question.min_value} and ${question.max_value}.`;
  } else if (question.min_value) {
    return `Please enter a value greater than ${question.min_value}.`;
  } else if (question.max_value) {
    return `Please enter a value less than ${question.max_value}.`;
  } else {
    return '';
  }
};

export const mapQuestionsToBenefits = (benefits: CategoryResponseDto) => {
  if (!benefits) {
    return new Map<number, number[]>();
  }

  const benefitMap = new Map<number, number[]>();

  // s/o to github copilot for writing this function
  benefits.data.benefits.forEach((benefit) => {
    benefitMap.set(
      benefit.benefit.org_benefit_id,
      benefit.org_benefit_survey_questions?.map(
        (question) => question.question_id
      ) || []
    );
  });

  return benefitMap;
};

export const parentQuestionAnswered = (
  parentQuestion: QuestionDto | undefined,
  question: QuestionDto,
  responses: string[]
) => {
  if (parentQuestion === undefined || responses === undefined) {
    return false;
  }

  let chosenResponse: any;

  switch (parentQuestion.question_type) {
    case NUMBER_SURVEY_TYPE:
      chosenResponse =
        Number(responses[0]) >= Number(parentQuestion.min_value) &&
        Number(responses[0]) <= Number(parentQuestion.max_value)
          ? responses[0]
          : undefined;

      if (!chosenResponse) {
        return false;
      }

      return question.trigger_answers?.some((answer) => {
        return Number(chosenResponse) === Number(answer);
      });

    case HEIGHT_SURVEY_TYPE:
      chosenResponse =
        Number(responses[0]) >= Number(parentQuestion.min_value) &&
        Number(responses[0]) <= Number(parentQuestion.max_value)
          ? responses[0]
          : undefined;
      if (!chosenResponse) {
        return false;
      }
      return question.trigger_answers?.some((answer) => {
        return Number(chosenResponse) === Number(answer);
      });

    default:
      chosenResponse = parentQuestion.valid_responses?.find((response) => {
        return responses.includes(String(response.response_id));
      });
      if (!chosenResponse) {
        return false;
      }
      return question.trigger_answers?.some((answer) => {
        return (
          String(chosenResponse.response_id) === String(answer) ||
          chosenResponse.value === answer
        );
      });
  }
};

export const hideSurveyModal = () => {
  const dialog = document.getElementById('questionnaire-form');
  const backdrop = document.querySelector('.modal-backdrop');
  const bodyTag = document.querySelector('body');
  dialog?.classList.remove('show');
  dialog?.classList.add('hide');
  dialog?.removeAttribute('style');
  backdrop?.remove();
  bodyTag?.classList.remove('modal-open');
  bodyTag?.removeAttribute('style');
};
